interface DateParts {
  year: number;
  month: number;
  day: number;
}

const DATE_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const isValidDateParts = ({ year, month, day }: DateParts): boolean => {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const validatedDate = new Date(Date.UTC(year, month - 1, day));
  return (
    validatedDate.getUTCFullYear() === year &&
    validatedDate.getUTCMonth() === month - 1 &&
    validatedDate.getUTCDate() === day
  );
};

const compareDateParts = (left: DateParts, right: DateParts): number => {
  if (left.year !== right.year) return left.year - right.year;
  if (left.month !== right.month) return left.month - right.month;
  return left.day - right.day;
};

const getTodayParts = (today: Date): DateParts => ({
  year: today.getFullYear(),
  month: today.getMonth() + 1,
  day: today.getDate(),
});

export const parseDateStringToParts = (value?: string | null): DateParts | null => {
  if (!value) return null;

  const trimmed = String(value).trim();
  if (!trimmed) return null;

  const prefixMatch = trimmed.match(DATE_PREFIX_PATTERN);
  if (prefixMatch) {
    const [, yearRaw, monthRaw, dayRaw] = prefixMatch;
    const parts: DateParts = {
      year: Number(yearRaw),
      month: Number(monthRaw),
      day: Number(dayRaw),
    };
    return isValidDateParts(parts) ? parts : null;
  }

  const parsedDate = new Date(trimmed);
  if (Number.isNaN(parsedDate.getTime())) return null;

  const fallbackParts: DateParts = {
    year: parsedDate.getFullYear(),
    month: parsedDate.getMonth() + 1,
    day: parsedDate.getDate(),
  };

  return isValidDateParts(fallbackParts) ? fallbackParts : null;
};

export const isDateOnOrBeforeToday = (value?: string | null, today = new Date()): boolean => {
  const dateParts = parseDateStringToParts(value);
  if (!dateParts) return false;

  return compareDateParts(dateParts, getTodayParts(today)) <= 0;
};

export const calculateAgeFromDateString = (value?: string | null, today = new Date()): number | null => {
  const birthDateParts = parseDateStringToParts(value);
  if (!birthDateParts) return null;

  const todayParts = getTodayParts(today);
  let age = todayParts.year - birthDateParts.year;

  if (
    todayParts.month < birthDateParts.month ||
    (todayParts.month === birthDateParts.month && todayParts.day < birthDateParts.day)
  ) {
    age -= 1;
  }

  return Math.max(0, age);
};

export const calculateAgeInMonthsFromDateString = (
  value?: string | null,
  today = new Date()
): number | null => {
  const birthDateParts = parseDateStringToParts(value);
  if (!birthDateParts) return null;

  const todayParts = getTodayParts(today);
  let months = (todayParts.year - birthDateParts.year) * 12 + (todayParts.month - birthDateParts.month);

  if (todayParts.day < birthDateParts.day) {
    months -= 1;
  }

  return months;
};

export const formatDateStringToYyyyMmDd = (value?: string | null): string => {
  const dateParts = parseDateStringToParts(value);
  if (!dateParts) return '';

  const month = String(dateParts.month).padStart(2, '0');
  const day = String(dateParts.day).padStart(2, '0');
  return `${dateParts.year}-${month}-${day}`;
};
